/**
 * Leads Service — Direct-API client helpers.
 *
 * Plain async functions backed by `directApiClient` — **not** server actions,
 * which are reserved for uploads, Server-Component reads and multi-step
 * orchestration. Usable from non-component callers as well as from the React
 * Query hooks in `./hooks`.
 *
 * Endpoints implemented:
 *   - `GET /leads` — the board read (LEADS-15 / LF-5982)
 *   - `POST /leads` — capture a new enquiry (LEADS-20 / LF-5987)
 *   - `GET /leads/duplicates` — duplicate awareness (LEADS-20 / LF-5987)
 *   - `GET /leads/:id` — one lead's detail (LEADS-21 / LF-5988)
 *   - `PATCH /leads/:id` — an inline field edit (LEADS-21 / LF-5988)
 *   - `POST /leads/:id/setup` — form, meeting, or both (LEADS-27 / LF-5994)
 *   - `POST|PATCH|DELETE /leads/:id/meetings[/:meetingId]` — meeting CRUD (LEADS-27)
 *   - `GET /leads/:id/meetings/calendar-invitation`: Outlook-sync availability (CAL-3)
 *   - `POST /leads/:id/close` · `/reopen` — the terminal decisions (LEADS-29 / LF-5996)
 *   - `POST` · `DELETE /leads/:id/outstanding[/:itemId]` — advisory items (LEADS-29 / LF-5996)
 *   - `GET|POST /leads/:id/cost-agreement[/send|/mark-signed|/precedents]` — the
 *     cost agreement (LEADS-45 / LF-6012)
 *
 * The rest of `technical-spec.md` §5 lands with the tickets that own each
 * endpoint; nothing is stubbed here ahead of its backend.
 */

import { directApiClient, DirectApiError } from '@/lib/api/direct-client';
import type { RefreshFromWordResult } from '@/lib/services/templates';
import {
  LEAD_DUPLICATE_MIN_TERM_LENGTH,
  type CaptureLeadInput,
  type CloseLeadInput,
  type ConvertLeadInput,
  type CreateLeadCostAgreementInput,
  type CreateLeadMeetingInput,
  type CreateLeadOutstandingItemInput,
  type AddLeadContactInput,
  type LeadContact,
  type UpdateLeadContactInput,
  type EditLeadCostAgreementInWordResponse,
  type LeadBoardFilters,
  type LeadBoardPage,
  type LeadBoardResponse,
  type LeadBoardSection,
  type LeadConvertResult,
  type LeadCostAgreementPrecedent,
  type LeadDetail,
  type LeadDocument,
  type LeadDuplicatesResponse,
  type LeadMeetingBriefResponse,
  type LeadMeetingCalendarAvailability,
  type LeadMeetingWriteResponse,
  type LeadOrigin,
  type LinkLeadMatterInput,
  type OverrideLeadStageInput,
  type PreRenderedLeadEmail,
  type PreRenderLeadEmailInput,
  type SendLeadCostAgreementInput,
  type SetupLeadInput,
  type SetupLeadResponse,
  type UpdateLeadInput,
  type UpdateLeadMeetingInput
} from './types';

/**
 * `GET /leads` — the board read. Every derived fact (section, effective stage,
 * stall, cost-agreement state, ordering) is resolved server-side; the response
 * is rendered as-is.
 *
 * Filters are passed through untouched. `directApiClient` serialises an array
 * as a **repeated** parameter (`?ownerId=2&ownerId=3`) and drops `undefined` /
 * `null` / `''`, which is exactly what `LeadBoardQueryDto` accepts — so no
 * hand-rolled query string is needed and none should be added.
 *
 * Throws `DirectApiError` on failure. A firm without the `leads` feature flag
 * gets a bare **404** (not a 403 — with the flag off the surface does not
 * exist for that firm, and a distinguishable error would leak an unreleased
 * feature to anyone probing).
 */
export async function fetchLeadBoard(
  filters?: LeadBoardFilters
): Promise<LeadBoardResponse> {
  return directApiClient.get<LeadBoardResponse>('/leads', {
    params: filters as Record<string, unknown> | undefined
  });
}

/**
 * Narrows a board response to the sectioned In-progress body.
 *
 * The response is bimodal and exactly one of `sections` / `page` is populated,
 * so every consumer needs this branch. Keyed on the echoed `outcome` rather
 * than on `sections !== null` because `outcome` is what the server actually
 * decided the shape from — testing the payload instead would make a future
 * empty-sections response silently read as a terminal tab.
 */
export function isOpenBoard(
  response: LeadBoardResponse
): response is LeadBoardResponse & { sections: LeadBoardSection[] } {
  return response.outcome === 'open' && response.sections !== null;
}

/**
 * Narrows a board response to a terminal tab's page. The mirror of
 * `isOpenBoard`, and keyed the same way.
 */
export function isTerminalBoard(
  response: LeadBoardResponse
): response is LeadBoardResponse & { page: LeadBoardPage } {
  return response.outcome !== 'open' && response.page !== null;
}

/**
 * `POST /leads` — capture a new enquiry (`functional-spec.md` §5.1).
 *
 * Returns the **full** `LeadDetail`, which is what lets the modal open the new
 * lead without a second read.
 *
 * The payload is passed through untouched. Empty optional strings are collapsed
 * by the caller rather than here, because the backend's own `blankToUndefined`
 * transform is the enforcement and duplicating a second, subtly different
 * version of that rule client-side is how the two drift.
 *
 * Throws `DirectApiError` on failure — including a bare **404** for a firm
 * without the `leads` feature flag.
 */
export async function captureLead(
  input: CaptureLeadInput
): Promise<LeadDetail> {
  return directApiClient.post<LeadDetail, CaptureLeadInput>('/leads', input);
}

/**
 * `GET /leads/:id` — one lead's full detail (`functional-spec.md` §4).
 *
 * The same `LeadDetail` shape `POST /leads` returns, which is why the capture
 * modal can seed `leadKeys.detail(id)` and have the drawer open on cached data.
 *
 * Every derived fact arrives settled (`derived`, `nextMeetingAt`, `closedAt`,
 * `hasIntakeForm`) — the drawer renders them and re-derives none of them.
 *
 * Throws `DirectApiError`: **404** for an unknown lead, for another firm's lead
 * that the ownership guard rejected, *and* for a firm without the `leads` flag —
 * all three deliberately indistinguishable.
 */
export async function fetchLead(leadId: number): Promise<LeadDetail> {
  return directApiClient.get<LeadDetail>(`/leads/${leadId}`);
}

/**
 * `PATCH /leads/:id` — one inline field edit (`functional-spec.md` §4.3).
 *
 * Returns the **whole** updated `LeadDetail`, not just the patched field, so the
 * caller can replace its cache entry outright rather than merging a partial
 * response into it. That matters because an edit can move a derived value — a
 * `netAssetValueCents` correction changes nothing derived, but an `enquiryAt`
 * one changes `daysInStage` — and a merge would leave the derived block
 * describing the pre-edit lead.
 *
 * **The patch is sent exactly as given.** An absent key leaves the column alone;
 * an explicit `null` clears it. In particular the value is **not trimmed** here:
 * §4.3 applies edits per character, so trimming would strip the space the moment
 * it is typed. The backend's own `emptyStringToNull` transform is the one place
 * `''` collapses to `null`, and restating that rule client-side is how the two
 * would drift.
 *
 * Throws `DirectApiError` — a **400** for a value the DTO rejects (an unknown
 * enum member, an empty `clientNames`, a field outside the whitelist), which is
 * what the caller surfaces while keeping the field open with the typing intact.
 */
export async function updateLead(
  leadId: number,
  patch: UpdateLeadInput
): Promise<LeadDetail> {
  return directApiClient.patch<LeadDetail, UpdateLeadInput>(
    `/leads/${leadId}`,
    patch
  );
}

/**
 * `GET /leads/duplicates` — duplicate awareness at capture (R1.6).
 *
 * **A warning, never a block.** The result never gates the capture: `POST
 * /leads` does not consult it, so a failure here must not stop the coordinator
 * saving the enquiry.
 *
 * Both terms are optional and are matched **independently**, then OR-ed — the
 * same family ringing back usually gives a different enquirer for the same
 * deceased. Terms below `LEAD_DUPLICATE_MIN_TERM_LENGTH` are ignored by the
 * backend; `hasSearchableDuplicateTerm` lets a caller skip the round trip
 * instead of firing one per keystroke that is known to come back empty.
 */
export async function fetchLeadDuplicates(params: {
  clientNames?: string;
  deceasedName?: string;
  email?: string;
  phone?: string;
}): Promise<LeadDuplicatesResponse> {
  return directApiClient.get<LeadDuplicatesResponse>('/leads/duplicates', {
    params: params as Record<string, unknown>
  });
}

/**
 * Is either term long enough for the duplicate search to act on?
 *
 * Mirrors the backend floor. Below it an `ILIKE '%term%'` matches most of the
 * board — "an" or "li" would warn about half the firm's leads — which trains
 * the coordinator to dismiss the warning without reading it, and a warning that
 * cries wolf is worse than none because the one real duplicate arrives already
 * ignored.
 */
export function hasSearchableDuplicateTerm(params: {
  clientNames?: string;
  deceasedName?: string;
  email?: string;
  phone?: string;
}): boolean {
  // An email or phone can name a firm contact on its own (VALE-2074): the
  // backend matches the email exactly and the phone on at least 6 digits.
  const emailUsable = !!params.email?.trim().includes('@');
  const phoneUsable = (params.phone?.replace(/\D/g, '').length ?? 0) >= 6;
  return (
    emailUsable ||
    phoneUsable ||
    [params.clientNames, params.deceasedName].some(
      (term) => (term?.trim().length ?? 0) >= LEAD_DUPLICATE_MIN_TERM_LENGTH
    )
  );
}

/**
 * `POST /leads/:id/close` — close the enquiry into a terminal band
 * (`functional-spec.md` §5.7, §4.7).
 *
 * **Both outcome decisions come through here.** `Mark proceeding` and
 * `Not proceeding…` are two calls to one endpoint, not two endpoints, because
 * both are closures and both must write the `closed` event the two terminal tabs
 * are ordered by.
 *
 * Returns the **whole** updated `LeadDetail`, so the caller replaces its cache
 * entry outright. That matters more here than on a field edit: closing moves
 * derived state the frontend must not recompute — the lead leaves its section,
 * `isStalled` goes false, and its position in the terminal tab is decided by the
 * closure this call just recorded.
 *
 * Throws `DirectApiError`: **400** `LEAD_NOT_PROCEEDING_REASON_REQUIRED` when a
 * not-proceeding closure carries no reason, **400**
 * `LEAD_PROCEEDING_TAKES_NO_REASON` when one is sent on the won branch, **409**
 * `LEAD_ALREADY_CLOSED` for an enquiry that already holds an outcome.
 */
export async function closeLead(
  leadId: number,
  input: CloseLeadInput
): Promise<LeadDetail> {
  return directApiClient.post<LeadDetail, CloseLeadInput>(
    `/leads/${leadId}/close`,
    input
  );
}

/**
 * `POST /leads/:id/reopen` — return a closed enquiry to the board (R9.4, §5.7).
 *
 * **The closure stays in history.** The `closed` event and its reason are never
 * deleted, so the activity timeline still shows that this enquiry was closed and
 * why; only the row's outcome columns clear. The enquiry resumes at the stage it
 * had reached, because closing never moved it.
 *
 * Throws `DirectApiError`: **409** `LEAD_NOT_CLOSED` for an enquiry that is
 * already open.
 */
export async function reopenLead(leadId: number): Promise<LeadDetail> {
  return directApiClient.post<LeadDetail>(`/leads/${leadId}/reopen`);
}

/**
 * `POST /leads/:id/stage` — set the status by hand (`functional-spec.md` §5.6).
 *
 * **§2.2's one carve-out**, for imported enquiries and genuine corrections. It
 * moves in **either direction**, unlike every artefact-driven stage move, because
 * a correction has to be able to walk an enquiry back — and it records a
 * `stage_overridden` activity row carrying both ends of the move, which is the
 * whole reason a hand-set status is auditable.
 *
 * Returns the **whole** updated `LeadDetail`, so the caller replaces its cache
 * entry outright. That matters as much here as on a close: the enquiry changes
 * board section, its `daysInStage` resets, and `effectiveStage` may now report the
 * derived `ready_to_book` rung — all derived facts the frontend must not recompute.
 *
 * Throws `DirectApiError`: **400** `LEAD_STAGE_UNCHANGED` when the enquiry already
 * holds that stage (refused rather than absorbed, because honouring it would
 * re-stamp the age the board sorts on), and a validation error for any value
 * outside the stored set — including `ready_to_book`, which is derived and never
 * stored.
 */
export async function overrideLeadStage(
  leadId: number,
  input: OverrideLeadStageInput
): Promise<LeadDetail> {
  return directApiClient.post<LeadDetail, OverrideLeadStageInput>(
    `/leads/${leadId}/stage`,
    input
  );
}

/**
 * `POST /leads/:id/flag` · `DELETE` — the board's one manual mark
 * (`functional-spec.md` §3.5.9).
 *
 * **Two verbs rather than a toggle route, which is why this takes the value it
 * wants rather than inverting one.** The caller states the outcome, so a click
 * retried on a flaky connection — or two of them racing — settles on the same
 * answer; asking the server to invert whatever it currently holds could settle
 * either way.
 *
 * **Nothing about the row's position changes** (§2.11). Floating flagged rows
 * was built and removed, because clicking a flag made the row teleport out from
 * under the cursor. The whole lead comes back all the same, because every lead
 * write on this service answers with the re-derived detail.
 */
export async function setLeadFlag(
  leadId: number,
  flagged: boolean
): Promise<LeadDetail> {
  return flagged
    ? directApiClient.post<LeadDetail>(`/leads/${leadId}/flag`)
    : directApiClient.delete<LeadDetail>(`/leads/${leadId}/flag`);
}

/**
 * `POST /leads/:id/allocation` · `DELETE` — hand the enquiry over for
 * allocation, and take it back.
 *
 * **Two verbs rather than a toggle**, the same reasoning as the flag above and
 * with more riding on it: a toggle inverted by a double-click would email the
 * firm's allocator and then silently un-queue the enquiry they had just been
 * told to pick up.
 *
 * **This is the band's one entry.** `Pending allocation` used to be derived from
 * four unrelated circumstances lining up, with no action anywhere behind it —
 * which is exactly what Coulter Legal could not work out. The exit is not here:
 * an enquiry leaves the band when it has a matter whose acting solicitor is set,
 * which is the allocator's own act of converting and naming who will run it.
 *
 * The whole lead comes back, like every other lead write on this service.
 */
export async function setLeadAllocation(
  leadId: number,
  requested: boolean
): Promise<LeadDetail> {
  return requested
    ? directApiClient.post<LeadDetail>(`/leads/${leadId}/allocation`)
    : directApiClient.delete<LeadDetail>(`/leads/${leadId}/allocation`);
}

/**
 * `POST /leads/:id/outstanding` — record something the firm is waiting on
 * (§2.5).
 *
 * **Advisory only.** It never gates a stage and writes no activity-log entry, so
 * nothing about the enquiry's pipeline position changes. The whole lead comes
 * back all the same, because the item belongs to the detail payload the drawer
 * renders from.
 */
export async function recordLeadOutstandingItem(
  leadId: number,
  input: CreateLeadOutstandingItemInput
): Promise<LeadDetail> {
  return directApiClient.post<LeadDetail, CreateLeadOutstandingItemInput>(
    `/leads/${leadId}/outstanding`,
    input
  );
}

/**
 * `DELETE /leads/:id/outstanding/:itemId` — clear an outstanding item.
 *
 * **Clearing is not deleting**: the row is kept and stamped `cleared_at`, so the
 * item leaves the drawer and the board row while the record of having chased it
 * survives. **Idempotent** — clearing an already-cleared item succeeds and does
 * not move the instant it was cleared at.
 *
 * Throws `DirectApiError`: **404** `LEAD_OUTSTANDING_ITEM_NOT_FOUND` for an item
 * that is not this enquiry's.
 */
export async function clearLeadOutstandingItem(
  leadId: number,
  itemId: number
): Promise<LeadDetail> {
  return directApiClient.delete<LeadDetail>(
    `/leads/${leadId}/outstanding/${itemId}`
  );
}

/**
 * `POST /leads/:id/contacts` — list a firm contact as one of the estate's LPRs
 * (VALE-2061). The contact drawer creates or finds the person first.
 *
 * Throws `DirectApiError`: **409** `LEAD_CONTACT_ALREADY_LISTED`.
 */
export async function addLeadContact(
  leadId: number,
  input: AddLeadContactInput
): Promise<LeadContact> {
  return directApiClient.post<LeadContact, AddLeadContactInput>(
    `/leads/${leadId}/contacts`,
    input
  );
}

/** `PATCH /leads/:id/contacts/:leadContactId` — correct whether they are an LPR, their role or relationship. */
export async function updateLeadContact(
  leadId: number,
  leadContactId: number,
  input: UpdateLeadContactInput
): Promise<LeadContact> {
  return directApiClient.patch<LeadContact, UpdateLeadContactInput>(
    `/leads/${leadId}/contacts/${leadContactId}`,
    input
  );
}

/** `DELETE /leads/:id/contacts/:leadContactId` — take a person off the enquiry. Their contact is kept. */
export async function removeLeadContact(
  leadId: number,
  leadContactId: number
): Promise<void> {
  await directApiClient.delete<void>(
    `/leads/${leadId}/contacts/${leadContactId}`
  );
}

/**
 * `POST /leads/:id/setup` — send the intake form, record the meeting, or both
 * (`functional-spec.md` §5.2).
 *
 * **The body is the selection.** Presence of `intakeForm` / `meeting` is what
 * picks the outcome, so the caller composes the halves it actually collected
 * rather than passing a mode. A body with neither is a 400.
 *
 * The response carries the whole re-read lead, so the dialog closes onto a
 * drawer that is already correct rather than racing a follow-up read.
 *
 * Throws `DirectApiError`: **400** for a refused confirmation
 * (`LEAD_MEETING_CONFIRMATION_INCOMPLETE`, naming the missing field), a `link`
 * on a non-video meeting, or a deceased block on a non-estate enquiry; **404**
 * for an unknown lead or a firm without the `leads` flag.
 */
export async function setupLead(
  leadId: number,
  input: SetupLeadInput
): Promise<SetupLeadResponse> {
  return directApiClient.post<SetupLeadResponse, SetupLeadInput>(
    `/leads/${leadId}/setup`,
    input
  );
}

/**
 * `POST /leads/:id/meetings` — record an appointment on its own.
 *
 * The same body as the setup call's meeting half, against the same backend write
 * path, so a meeting booked from the row's `+ Meeting` pill and one booked as
 * part of a full setup cannot drift.
 */
export async function createLeadMeeting(
  leadId: number,
  input: CreateLeadMeetingInput
): Promise<LeadMeetingWriteResponse> {
  return directApiClient.post<LeadMeetingWriteResponse, CreateLeadMeetingInput>(
    `/leads/${leadId}/meetings`,
    input
  );
}

/**
 * `GET /leads/:id/meetings/calendar-invitation`: would a booking made now go
 * out as an Outlook calendar invitation (CAL-3)? `bookedSolicitorId` is the
 * solicitor the dialog has selected; omitted, the backend uses the lead's
 * preferred solicitor, who a booking naming nobody would go to.
 */
export async function fetchLeadMeetingCalendarAvailability(
  leadId: number,
  bookedSolicitorId: number | null
): Promise<LeadMeetingCalendarAvailability> {
  return directApiClient.get<LeadMeetingCalendarAvailability>(
    `/leads/${leadId}/meetings/calendar-invitation`,
    bookedSolicitorId != null ? { params: { bookedSolicitorId } } : undefined
  );
}

/**
 * `PATCH /leads/:id/meetings/:meetingId` — §5.3's narrower edit dialog.
 *
 * The patch is sent as given: an absent key leaves the column alone, an explicit
 * `null` clears it. An edit does **not** move the stage — the artefact already
 * moved it when it was recorded.
 */
export async function updateLeadMeeting(
  leadId: number,
  meetingId: number,
  patch: UpdateLeadMeetingInput
): Promise<LeadMeetingWriteResponse> {
  return directApiClient.patch<
    LeadMeetingWriteResponse,
    UpdateLeadMeetingInput
  >(`/leads/${leadId}/meetings/${meetingId}`, patch);
}

/**
 * `POST /leads/:id/meetings/:meetingId/brief` — push the pre-meeting brief now.
 *
 * The on-demand half of §6.2's *"scheduled and on demand"*: a daily pass already
 * pushes the brief up to 48 hours out, and this is what covers a meeting moved over the
 * phone (where the scheduled trigger has already misfired) or a solicitor who
 * wants it early.
 *
 * Re-sending an already-briefed meeting is allowed and is the point — the server
 * re-stamps `briefSentAt` rather than refusing.
 */
export async function sendLeadMeetingBrief(
  leadId: number,
  meetingId: number
): Promise<LeadMeetingBriefResponse> {
  return directApiClient.post<LeadMeetingBriefResponse, undefined>(
    `/leads/${leadId}/meetings/${meetingId}/brief`,
    undefined
  );
}

/**
 * `DELETE /leads/:id/meetings/:meetingId` — the appointment did not happen.
 *
 * Soft-deleted server-side, and the stage is **not** walked back: a cancelled
 * first appointment does not unwind the enquiry, and the `meeting_recorded`
 * timeline entry stays because it is true. Returns the refreshed lead.
 */
export async function deleteLeadMeeting(
  leadId: number,
  meetingId: number
): Promise<LeadDetail> {
  return directApiClient.delete<LeadDetail>(
    `/leads/${leadId}/meetings/${meetingId}`
  );
}

/**
 * `POST /leads/:id/convert` — mint a matter from this enquiry and link it
 * (R9.1, LF-6021 / LEADS-50, `functional-spec.md` §5.8).
 *
 * The body is normally empty — one click is the whole interaction.
 * `confirmedDeceasedName` exists only for the inline retry after a
 * **422** `DECEASED_NAME_REQUIRED`.
 *
 * **Idempotent** (`technical-spec.md` §4.1): a duplicate submit silently
 * replays the first call's response rather than 409ing or minting a second
 * matter.
 *
 * Throws `DirectApiError`: **422** `DECEASED_NAME_REQUIRED` (retry with
 * `confirmedDeceasedName`) or `FIRM_JURISDICTION_MISSING`; **409**
 * `LEAD_MATTER_DIVERGENCE` (`error.response.matterIds` names the offending
 * matter(s) — an artefact on this lead is already linked elsewhere).
 */
export async function convertLead(
  leadId: number,
  input: ConvertLeadInput = {}
): Promise<LeadConvertResult> {
  return directApiClient.post<LeadConvertResult, ConvertLeadInput>(
    `/leads/${leadId}/convert`,
    input
  );
}

/**
 * `POST /leads/:id/link-matter` — associate an existing matter (R9.5).
 *
 * Unlike convert, this is **not** wrapped in the create-side idempotency
 * lock: it creates nothing, so a repeat submit is refused as already-linked
 * rather than replayed.
 *
 * Throws `DirectApiError`: **409** `LEAD_ALREADY_LINKED` (this lead already
 * holds a matter) or `LEAD_MATTER_DIVERGENCE` (an artefact is bound to a
 * *different* matter than the one being linked); **404** for a `matterId`
 * outside the caller's firm.
 */
export async function linkLeadMatter(
  leadId: number,
  input: LinkLeadMatterInput
): Promise<LeadConvertResult> {
  return directApiClient.post<LeadConvertResult, LinkLeadMatterInput>(
    `/leads/${leadId}/link-matter`,
    input
  );
}

/** The coded errors the convert / link-matter surface switches on. */
export type LeadConvertErrorCode =
  | 'DECEASED_NAME_REQUIRED'
  | 'FIRM_JURISDICTION_MISSING'
  | 'LEAD_MATTER_DIVERGENCE'
  | 'LEAD_ALREADY_LINKED';

/** Extract the backend error `code` from a thrown `DirectApiError`, if present. */
export function leadConvertErrorCode(
  error: unknown
): LeadConvertErrorCode | undefined {
  if (error instanceof DirectApiError) {
    return error.response?.code as LeadConvertErrorCode | undefined;
  }
  return undefined;
}

/**
 * The offending matter id(s) on a `LEAD_MATTER_DIVERGENCE` — so the caller
 * can name which matter to unbind an artefact from.
 */
export function leadDivergenceMatterIds(error: unknown): number[] {
  if (error instanceof DirectApiError) {
    return error.response?.matterIds ?? [];
  }
  return [];
}

/**
 * `GET /leads/by-matter/:matterId` — the matter-side "origin lead" chip's one
 * read (LF-6021 / LEADS-50). `null` is the normal case: most matters were
 * never a lead.
 *
 * **The `?? null` is load-bearing (VALE-1057).** The backend answers "no
 * origin lead" with an empty body, which `directApiClient` maps to
 * `undefined` — React Query rejects `undefined` outright, so the query would
 * error and retry three times on the vast majority of matters. Same defect
 * class, same fix, as `useZapierMatterLink` (VALE-933).
 */
export async function fetchLeadOriginForMatter(
  matterId: number
): Promise<LeadOrigin | null> {
  return (
    (await directApiClient.get<LeadOrigin | null>(
      `/leads/by-matter/${matterId}`
    )) ?? null
  );
}

/**
 * `POST /leads/:id/emails/pre-render` — render one of the lead's Vale emails for
 * the review step (§5.2). **A read expressed as a POST**: the body carries the
 * unsaved meeting the preview is of, and nothing is written server-side.
 *
 * Throws `DirectApiError`: **400** for the same three refusals the send makes
 * (`LEAD_MEETING_CONFIRMATION_INCOMPLETE` — recipient, solicitor, format), which
 * is deliberate. The dialog's own summary shows those as amber "Still needed"
 * rows, so a preview that succeeded where the send would fail — or failed
 * silently — would put the two one step out of sync.
 */
export async function preRenderLeadEmail(
  leadId: number,
  input: PreRenderLeadEmailInput
): Promise<PreRenderedLeadEmail> {
  return directApiClient.post<PreRenderedLeadEmail, PreRenderLeadEmailInput>(
    `/leads/${leadId}/emails/pre-render`,
    input
  );
}

// =============================================================================
// The cost agreement — LF-6012 / LEADS-45, `functional-spec.md` §4.10 / §5.5
// =============================================================================

/**
 * `GET /leads/:id/cost-agreement/precedents` — what the Configure step's picker
 * offers.
 *
 * Lead-scoped rather than read from the templates library, and not for tidiness:
 * `costs-agreement` is a non-production-ready document type, so the library's
 * list filters it out for every firm without `experimental-precedent-types` (and
 * 404s the typed call). A leads pilot firm has no reason to hold that separate
 * flag, so a picker fed from there would be empty on exactly the firms this
 * ships to — while drafting would render happily.
 *
 * An **empty array** is a real answer, not a failure: this firm has authored no
 * costs-agreement precedent yet, so the precedent door has nothing to offer and
 * the card says so.
 */
export async function fetchLeadCostAgreementPrecedents(
  leadId: number
): Promise<LeadCostAgreementPrecedent[]> {
  return directApiClient.get<LeadCostAgreementPrecedent[]>(
    `/leads/${leadId}/cost-agreement/precedents`
  );
}

/**
 * `POST /leads/:id/cost-agreement` — draft the agreement, through whichever of
 * §4.10's **two doors** the solicitor took.
 *
 * `multipart/form-data` on both, because the upload door attaches a `.docx`; the
 * precedent door simply sends no file. Assembling the `FormData` here rather
 * than at the call site is what keeps "which fields go on the wire" in one
 * place — and `directApiClient` passes a `FormData` body through untouched
 * (LF-6012), so this needs no hand-rolled `fetch`.
 *
 * **The document is produced now, not at send** (LEADS-44), so every way the
 * render can fail surfaces here — before the solicitor is told the agreement is
 * ready, and before any envelope has been paid for.
 *
 * Throws `DirectApiError`: **400** `LEAD_COST_AGREEMENT_MISSING_SIGNATURE_BLOCK`
 * (the uploaded document has no `<<signatureBlock>>`, so there is nowhere to
 * sign); **409** `LEAD_COST_AGREEMENT_BLOCKED_ON_DATA` (the template asks for
 * data the enquiry does not hold — `error.response.missingRequired` lists the
 * fields) or `LEAD_COST_AGREEMENT_GENERATION_FAILED`.
 */
export async function createLeadCostAgreement(
  leadId: number,
  input: CreateLeadCostAgreementInput
): Promise<LeadDetail> {
  const form = new FormData();
  form.append('source', input.source);
  // Absent, not empty: the backend's `templateId` is optional and an empty
  // string would fail its `@IsInt()` rather than meaning "use the default".
  if (input.templateId !== undefined) {
    form.append('templateId', String(input.templateId));
  }
  // Same rule for the Vale-standard reference: absent rather than empty, since
  // the backend's `@IsNotEmpty()` would reject `''` instead of reading it as
  // "use the default". The two are mutually exclusive by construction — the
  // picker yields exactly one of them per option.
  if (input.systemKey !== undefined) {
    form.append('systemKey', input.systemKey);
  }
  /*
    Each door sends only its own field. The backend ignores a stray `file` on the
    precedent door and a stray `templateId` on the upload one, so neither is a
    bug — but a document that rides along and is silently discarded while the
    solicitor believes it is the one being sent is worth making unreachable at
    the single place that assembles this body.
  */
  if (input.source === 'upload' && input.file) {
    form.append('file', input.file);
  }

  return directApiClient.post<LeadDetail, FormData>(
    `/leads/${leadId}/cost-agreement`,
    form
  );
}

/**
 * `POST /leads/:id/cost-agreement/send` — the one action in the product that
 * creates an Annature envelope for a lead.
 *
 * Each envelope costs real money and `createEnvelope` has no retry, so this must
 * only ever fire on an explicit user action — never on mount, never on a retry,
 * never optimistically.
 *
 * Throws `DirectApiError`: **409** `LEAD_COST_AGREEMENT_ALREADY_SENT` (a second
 * send would bill twice and leave two envelopes racing to record one signature —
 * revised terms are a *new* agreement), `LEAD_COST_AGREEMENT_ALREADY_SIGNED`, or
 * `LEAD_COST_AGREEMENT_UNDELIVERABLE` (no recipient address, checked *before*
 * the envelope is created).
 */
export async function sendLeadCostAgreement(
  leadId: number,
  input: SendLeadCostAgreementInput = {}
): Promise<LeadDetail> {
  return directApiClient.post<LeadDetail, SendLeadCostAgreementInput>(
    `/leads/${leadId}/cost-agreement/send`,
    input
  );
}

/**
 * `POST /leads/:id/cost-agreement/mark-signed` — record a signature obtained
 * outside Vale.
 *
 * The fallback for an agreement signed on paper. Once the completion webhook
 * lands it sets `signedAt` itself and the card stops offering this — which is
 * the point: it is the cost of not doing e-sign, not a feature of doing it.
 */
export async function markLeadCostAgreementSigned(
  leadId: number
): Promise<LeadDetail> {
  return directApiClient.post<LeadDetail>(
    `/leads/${leadId}/cost-agreement/mark-signed`
  );
}

/**
 * `POST /leads/:id/cost-agreement/edit-in-word` — open the drafted agreement in
 * Word (LF-6159).
 *
 * **Amends the document the two doors produced; it is not a third door.** The
 * backend refuses once the agreement has been sent — from then on Annature holds
 * the bytes the client is signing — so the card offers this only while drafted.
 *
 * Idempotent: calling it again while a working copy exists reopens that copy
 * with a fresh URL rather than provisioning a second one, so a reload, a return
 * later, or a colleague all land in the same document.
 */
export async function editLeadCostAgreementInWord(
  leadId: number
): Promise<EditLeadCostAgreementInWordResponse> {
  return directApiClient.post<EditLeadCostAgreementInWordResponse>(
    `/leads/${leadId}/cost-agreement/edit-in-word`
  );
}

/**
 * `POST /leads/:id/cost-agreement/refresh-from-word` — pull a committed Word
 * edit into the drafted agreement now, rather than waiting on the ~30s change
 * notification (VALE-288).
 *
 * Never rejects: the read seam is an accelerator over that notification, and the
 * send still refuses outright on a sync it cannot prove, so a transport failure
 * here must not surface as a broken action. It does report what happened,
 * because a check that could not reach Word looks exactly like a clean one that
 * found nothing, and a lawyer reads that silence as "Vale has my latest".
 */
export async function refreshLeadCostAgreementFromWord(
  leadId: number
): Promise<RefreshFromWordResult> {
  try {
    return await directApiClient.post<RefreshFromWordResult>(
      `/leads/${leadId}/cost-agreement/refresh-from-word`
    );
  } catch {
    return { status: 'failed' };
  }
}

/** The coded errors the cost-agreement surface switches on. */
export type LeadCostAgreementErrorCode =
  | 'LEAD_COST_AGREEMENT_MISSING_SIGNATURE_BLOCK'
  | 'LEAD_COST_AGREEMENT_BLOCKED_ON_DATA'
  | 'LEAD_COST_AGREEMENT_GENERATION_FAILED'
  | 'LEAD_COST_AGREEMENT_ALREADY_SENT'
  | 'LEAD_COST_AGREEMENT_ALREADY_SIGNED'
  | 'LEAD_COST_AGREEMENT_UNDELIVERABLE'
  | 'LEAD_COST_AGREEMENT_NOT_FOUND'
  | 'LEAD_COST_AGREEMENT_NOT_EDITABLE'
  | 'LEAD_COST_AGREEMENT_NO_EDITABLE_DOCUMENT';

/** Extract the backend error `code` from a thrown `DirectApiError`, if present. */
export function leadCostAgreementErrorCode(
  error: unknown
): LeadCostAgreementErrorCode | undefined {
  if (error instanceof DirectApiError) {
    return error.response?.code as LeadCostAgreementErrorCode | undefined;
  }
  return undefined;
}

/**
 * The merge fields a `LEAD_COST_AGREEMENT_BLOCKED_ON_DATA` names.
 *
 * Surfaced rather than swallowed because the refusal is *informative*: the
 * common pre-matter case is a precedent referencing the required
 * `<<lpr.names>>`, and telling the solicitor exactly which fields the enquiry
 * cannot fill is what turns "it didn't work" into "author a precedent on the
 * `lead.*` namespace".
 */
export function leadCostAgreementMissingFields(error: unknown): string[] {
  if (error instanceof DirectApiError) {
    return error.response?.missingRequired ?? [];
  }
  return [];
}

// =============================================================================
// Client-supplied documents — LF-6029 / LEADS-53
// =============================================================================

/**
 * `GET /leads/:id/documents` — the scanned originals held on this enquiry.
 *
 * An **empty array** is a real answer, not a failure: most enquiries carry no
 * documents until the first meeting happens.
 *
 * The lead's generated cost agreement is also lead-owned but is deliberately not
 * in this list — it is the firm's own artefact, rendered by its own card, not
 * something the client handed over.
 */
export async function fetchLeadDocuments(
  leadId: number
): Promise<LeadDocument[]> {
  return directApiClient.get<LeadDocument[]>(`/leads/${leadId}/documents`);
}

/**
 * `POST /leads/:id/documents/mark-seen` — somebody looked at the enquiry's
 * documents. Clears the board's "New documents" signal
 * (`derived.hasUnseenClientDocuments`). `204`; ignored server-side under
 * impersonation.
 */
export async function markLeadDocumentsSeen(leadId: number): Promise<void> {
  await directApiClient.post<void>(`/leads/${leadId}/documents/mark-seen`);
}

/**
 * `DELETE /leads/:id/documents/:fileId` — remove one.
 *
 * A **soft delete** server-side, not a detach: one call takes the document off
 * the card, out of the lead's pre-matter AI context, and out of the convert
 * sweep. There is no undo, which is why the card confirms nothing and instead
 * keeps the affordance quiet (the shipped `FileRow` remove icon).
 */
export async function deleteLeadDocument(
  leadId: number,
  fileId: number
): Promise<void> {
  await directApiClient.delete<void>(`/leads/${leadId}/documents/${fileId}`);
}

/**
 * `DELETE /leads/:id` — delete the enquiry itself (VALE-1616).
 *
 * A **soft delete** server-side: the row and everything the client supplied are
 * retained and recoverable by Vale, they simply leave every list, tab, count and
 * sweep in the product. `204`, so there is nothing to return — the caller
 * invalidates rather than reconciling a row that no longer renders anywhere.
 *
 * Rejects with `LEAD_HAS_MATTER` when the enquiry became a live matter. The menu
 * omits the affordance in that state, so this is the second layer.
 */
export async function deleteLead(leadId: number): Promise<void> {
  await directApiClient.delete<void>(`/leads/${leadId}`);
}
